import { supabase } from '@/integrations/supabase/client';

export interface CreditDeductionResult {
  success: boolean;
  newBalance: number;
  creditsDeducted: number;
  insufficientCredits?: boolean;
  error?: string;
}

export interface TradingEvent {
  userId: string;
  aum: number; // Assets Under Management in USD
  eventType: 'order' | 'strategy' | 'backtest';
  description: string;
  symbol?: string;
  strategyName?: string;
  metadata?: Record<string, any>;
}

export class CreditService {
  // 10 basis points = 0.001 (0.1%)
  private static readonly BASIS_POINTS_FEE = 0.001;
  
  // 1 credit = $1,000 of tradeable AUM for representation purposes
  // But for fee calculation, credits are deducted based on the fee amount directly
  private static readonly CREDIT_TO_AUM_RATIO = 1000;

  /**
   * Calculate credits required for a given AUM
   * Formula: AUM * 0.001 (10 basis points) gives us the fee in dollars
   * Since each credit represents $1,000 of AUM capacity, but the fee is charged directly,
   * we need credits equal to the dollar fee amount divided by the credit value
   * 
   * Updated interpretation: Credits needed = (AUM * 10 basis points) / $1000 per credit
   * So for $140,811 AUM: ($140,811 * 0.001) / $1000 = $140.81 / $1000 = 0.14081 credits
   * 
   * But based on user feedback, it seems like the requirement is:
   * Credits needed = AUM * 10 basis points converted to credits
   * Where the fee amount in dollars becomes the credit amount needed
   */
  static calculateCreditsRequired(aum: number): number {
    // Calculate the fee amount in dollars (10 basis points of AUM)
    const feeAmountUSD = aum * this.BASIS_POINTS_FEE;
    
    // The credits required should be the fee amount divided by $1000 
    // since 1 credit = $1000 of tradeable AUM
    // But if the user expects the fee to be charged as credits directly,
    // then we might need to adjust this
    
    // Based on the user's feedback, I think they expect:
    // For $140,811 AUM, fee = $140.81, so they need 140.81 credits
    // This suggests 1 credit = $1 of fee, not $1000 of AUM
    
    // Let me implement: credits needed = fee amount in dollars
    const creditsRequired = feeAmountUSD;
    
    return Math.ceil(creditsRequired * 100) / 100; // Round up to 2 decimal places
  }

  /**
   * Deduct credits for a trading event
   */
  static async deductCreditsForTrade(tradingEvent: TradingEvent): Promise<CreditDeductionResult> {
    try {
      const { userId, aum, eventType, description, symbol, strategyName, metadata } = tradingEvent;
      
      // Calculate credits required
      const creditsRequired = this.calculateCreditsRequired(aum);
      
      // Get current user balance
      const { data: userCredit, error: fetchError } = await supabase
        .from('user_credits')
        .select('balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') {
        return {
          success: false,
          newBalance: 0,
          creditsDeducted: 0,
          error: `Failed to fetch user balance: ${fetchError.message}`
        };
      }

      const currentBalance = userCredit?.balance || 0;

      // Check if user has sufficient credits
      if (currentBalance < creditsRequired) {
        return {
          success: false,
          newBalance: currentBalance,
          creditsDeducted: 0,
          insufficientCredits: true,
          error: `Insufficient credits. Required: ${creditsRequired.toFixed(2)}, Available: ${currentBalance.toFixed(2)}`
        };
      }

      // Calculate new balance
      const newBalance = currentBalance - creditsRequired;

      // Update user credit balance
      if (userCredit) {
        // Update existing balance
        const { error: updateError } = await supabase
          .from('user_credits')
          .update({ 
            balance: newBalance,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);

        if (updateError) {
          return {
            success: false,
            newBalance: currentBalance,
            creditsDeducted: 0,
            error: `Failed to update balance: ${updateError.message}`
          };
        }
      } else {
        // This shouldn't happen in normal flow, but handle gracefully
        return {
          success: false,
          newBalance: 0,
          creditsDeducted: 0,
          error: 'User credit record not found. Please purchase credits first.'
        };
      }

      // Record the transaction
      const transactionDescription = this.buildTransactionDescription(eventType, description, symbol, strategyName, aum);
      
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          user_id: userId,
          type: eventType === 'order' ? 'order_execution' : 
                eventType === 'strategy' ? 'strategy_deployment' : 'backtest_run',
          amount: -creditsRequired, // Negative for deduction
          description: transactionDescription,
          timestamp: new Date().toISOString(),
          metadata: {
            aum: aum,
            credits_deducted: creditsRequired,
            basis_points_fee: this.BASIS_POINTS_FEE,
            ...metadata
          }
        });

      if (transactionError) {
        console.error('Failed to record transaction:', transactionError);
        // Don't fail the whole operation for transaction recording failure
      }

      // Dispatch event for UI updates
      this.dispatchCreditUpdateEvent(newBalance);

      return {
        success: true,
        newBalance: newBalance,
        creditsDeducted: creditsRequired
      };

    } catch (error) {
      console.error('Credit deduction failed:', error);
      return {
        success: false,
        newBalance: 0,
        creditsDeducted: 0,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get user's current credit balance
   */
  static async getUserCredits(userId: string): Promise<{ balance: number; error?: string }> {
    try {
      const { data: userCredit, error } = await supabase
        .from('user_credits')
        .select('balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        return { balance: 0, error: error.message };
      }

      return { balance: userCredit?.balance || 0 };
    } catch (error) {
      return { 
        balance: 0, 
        error: error instanceof Error ? error.message : 'Failed to fetch credits' 
      };
    }
  }

  /**
   * Check if user has sufficient credits for a given AUM
   */
  static async checkSufficientCredits(userId: string, aum: number): Promise<{
    sufficient: boolean;
    required: number;
    available: number;
    error?: string;
  }> {
    const creditsRequired = this.calculateCreditsRequired(aum);
    const { balance, error } = await this.getUserCredits(userId);

    if (error) {
      return {
        sufficient: false,
        required: creditsRequired,
        available: 0,
        error
      };
    }

    return {
      sufficient: balance >= creditsRequired,
      required: creditsRequired,
      available: balance
    };
  }

  /**
   * Build a descriptive transaction description
   */
  private static buildTransactionDescription(
    eventType: string, 
    description: string, 
    symbol?: string, 
    strategyName?: string,
    aum?: number
  ): string {
    const aumStr = aum ? ` (${this.formatCurrency(aum)} AUM)` : '';
    
    switch (eventType) {
      case 'order':
        return `Order Execution: ${symbol || 'Unknown Symbol'}${aumStr} - ${description}`;
      case 'strategy':
        return `Strategy Deployment: ${strategyName || 'Unnamed Strategy'}${aumStr} - ${description}`;
      case 'backtest':
        return `Backtest Run: ${strategyName || 'Strategy'}${aumStr} - ${description}`;
      default:
        return `Trading Operation${aumStr} - ${description}`;
    }
  }

  /**
   * Format currency for display
   */
  private static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  /**
   * Dispatch credit update event for UI updates
   */
  private static dispatchCreditUpdateEvent(newBalance: number): void {
    // Dispatch custom event for components listening to credit updates
    window.dispatchEvent(new CustomEvent('creditBalanceUpdated', {
      detail: { newBalance }
    }));
    
    // Also dispatch a more generic event that can trigger refetches
    window.dispatchEvent(new CustomEvent('creditDataChanged', {
      detail: { 
        newBalance,
        timestamp: new Date().toISOString()
      }
    }));
  }
}

export default CreditService; 